import { useState, useEffect, useCallback, useMemo } from "react";
import { Layers, Loader2, Hammer, Save, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { DEFAULT_PACKAGE, PAGE_COUNTS, PRICE_TIERS, pricePackage, simulate, money } from "@/lib/landingSim";
import { GrowthChart, SimTotals } from "@/components/webadvisor/GrowthChart";
import { LandingPagesTable } from "@/components/webadvisor/LandingPagesTable";
import { PricePresets } from "@/components/webadvisor/PricePresets";

const errMsg = (err, f) => { const d = err.response?.data?.detail; return Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || f; };
const PICK = Object.keys(DEFAULT_PACKAGE);

const Num = ({ label, value, onChange, disabled, testId, step = "1" }) => (
  <div><Label className="text-xs">{label}</Label><Input type="number" step={step} min="0" value={value} disabled={disabled} onChange={e => onChange(e.target.value)} className="h-9" data-testid={testId} /></div>
);

const PricingBlock = ({ cfg, set, ro, price }) => (
  <div className="space-y-3">
    <div>
      <Label className="text-xs">Landing pages</Label>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {PAGE_COUNTS.map(n => (
          <button key={n} disabled={ro} onClick={() => set("page_count", n)} className={`text-xs font-bold px-3 py-1.5 rounded-full border ${Number(cfg.page_count) === n ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`} data-testid={`pkg-count-${n}`}>{n}</button>
        ))}
        <Input type="number" min="1" disabled={ro} value={cfg.page_count} onChange={e => set("page_count", e.target.value)} className="h-8 w-24 text-xs" placeholder="100+" data-testid="pkg-count-custom" />
      </div>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div><Label className="text-xs">Price per page</Label>
        <Select value={cfg.price_tier} onValueChange={v => set("price_tier", v)} disabled={ro}>
          <SelectTrigger className="h-9" data-testid="pkg-tier"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="retail">Retail {money(PRICE_TIERS.retail)}</SelectItem><SelectItem value="wholesale">Wholesale {money(PRICE_TIERS.wholesale)}</SelectItem><SelectItem value="custom">Custom price</SelectItem></SelectContent>
        </Select>
      </div>
      {cfg.price_tier === "custom" && <Num label="Custom $ / page" step="0.01" value={cfg.custom_unit_price} onChange={v => set("custom_unit_price", v)} disabled={ro} testId="pkg-custom-price" />}
      <Num label="Markup %" value={cfg.markup_pct} onChange={v => set("markup_pct", v)} disabled={ro} testId="pkg-markup" />
      <Num label="Discount %" value={cfg.discount_pct} onChange={v => set("discount_pct", v)} disabled={ro} testId="pkg-discount" />
    </div>
    <div className="flex flex-wrap items-end justify-between gap-3 bg-blue-50 border border-blue-100 rounded-lg p-3" data-testid="pkg-price-summary">
      <p className="text-sm text-slate-700">{cfg.page_count} pages × {money(price.unit_price)}{price.discount > 0 && <> − {money(price.discount)} discount</>}</p>
      <p className="text-2xl font-black text-blue-700" data-testid="pkg-total">{money(price.total)}</p>
    </div>
  </div>
);

const SimBlock = ({ cfg, set, ro, sim, cost }) => (
  <div className="space-y-3">
    <div>
      <div className="flex justify-between"><Label className="text-xs">Monthly growth rate</Label><span className="text-sm font-bold text-blue-700" data-testid="pkg-growth-value">{cfg.growth_rate_pct}%</span></div>
      <Slider value={[Number(cfg.growth_rate_pct)]} min={0} max={30} step={1} disabled={ro} onValueChange={([v]) => set("growth_rate_pct", v)} className="mt-2" data-testid="pkg-growth-slider" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <Num label="Visits / page / mo" value={cfg.visits_per_page} onChange={v => set("visits_per_page", v)} disabled={ro} testId="pkg-visits" />
      <Num label="Lead conversion %" step="0.1" value={cfg.conversion_pct} onChange={v => set("conversion_pct", v)} disabled={ro} testId="pkg-conv" />
      <Num label="Close rate %" value={cfg.close_rate_pct} onChange={v => set("close_rate_pct", v)} disabled={ro} testId="pkg-close" />
      <Num label="Avg ticket $" value={cfg.avg_ticket} onChange={v => set("avg_ticket", v)} disabled={ro} testId="pkg-ticket" />
      <Num label="Months to rank" value={cfg.ramp_months} onChange={v => set("ramp_months", v)} disabled={ro} testId="pkg-ramp" />
    </div>
    <GrowthChart sim={sim} />
    <SimTotals sim={sim} cost={cost} />
  </div>
);

export const LandingPackage = ({ audit }) => {
  const { user } = useAuth();
  const ro = user?.role !== "admin";
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(DEFAULT_PACKAGE);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get(`/api/webadvisor/package/${audit.id}`).then(r => {
    setData(r.data);
    if (r.data.package) setCfg(Object.fromEntries(PICK.map(k => [k, r.data.package[k] ?? DEFAULT_PACKAGE[k]])));
  }).catch(() => {}), [audit.id]);
  useEffect(() => { load(); }, [load]);
  const building = data?.package?.build_status === "building";
  useEffect(() => { if (!building) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [building, load]);

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const price = useMemo(() => pricePackage(cfg), [cfg]);
  const sim = useMemo(() => simulate(cfg, price.total), [cfg, price.total]);
  const numeric = (c) => Object.fromEntries(Object.entries(c).map(([k, v]) => [k, typeof DEFAULT_PACKAGE[k] === "number" ? Number(v) : v]));
  const dirty = !ro && PICK.some(k => String(numeric(cfg)[k]) !== String(data?.package ? (data.package[k] ?? DEFAULT_PACKAGE[k]) : DEFAULT_PACKAGE[k])) || (!ro && data && !data.package);

  const save = async () => {
    setBusy("save");
    try { await api.put(`/api/webadvisor/package/${audit.id}`, numeric(cfg)); toast.success("Package saved — quote updated"); load(); window.dispatchEvent(new Event("wa-quote-changed")); }
    catch (err) { toast.error(errMsg(err, "Save failed")); } finally { setBusy(""); }
  };
  const build = async () => {
    setBusy("build");
    try { await api.put(`/api/webadvisor/package/${audit.id}`, numeric(cfg)); await api.post(`/api/webadvisor/package/${audit.id}/build`); toast.success("Building pages — this runs in the background"); load(); window.dispatchEvent(new Event("wa-quote-changed")); }
    catch (err) { toast.error(errMsg(err, "Build failed")); } finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this package from the proposal? Built pages are kept.")) return;
    await api.delete(`/api/webadvisor/package/${audit.id}`); setData(d => ({ ...d, package: null })); toast.success("Package removed");
  };

  if (!data) return null;
  const pkg = data.package;
  const pagesCount = pkg?.pages?.length || 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-5" data-testid="landing-package">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Layers className="w-5 h-5 text-blue-600" />Landing Page Package</h2>
          <p className="text-sm text-slate-500">Boost visibility with SEO landing pages, set up and hosted.{ro && " Only admins can change the package."}</p>
        </div>
        {!ro && (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-2"><Switch checked={cfg.delivery === "build"} onCheckedChange={v => set("delivery", v ? "build" : "proposal_only")} data-testid="pkg-delivery" />{cfg.delivery === "build" ? "Build & host real pages" : "Proposal line only"}</label>
            <label className="flex items-center gap-2"><Switch checked={cfg.include_in_proposal} onCheckedChange={v => set("include_in_proposal", v)} data-testid="pkg-in-proposal" />In proposal</label>
          </div>
        )}
      </div>
      <PricePresets cfg={cfg} ro={ro} onApply={(patch) => setCfg(c => ({ ...c, ...patch }))} />
      <PricingBlock cfg={cfg} set={set} ro={ro} price={price} />
      <div className="border-t border-slate-100 pt-4"><p className="font-semibold text-slate-900 mb-2">Traffic growth simulation</p><SimBlock cfg={cfg} set={set} ro={ro} sim={sim} cost={price.total} /></div>
      {dirty && <p className="text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="pkg-unsaved">Unsaved changes — click Save package to update the quote, invoice and PDF.</p>}
      {!ro && (
        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
          <button onClick={save} disabled={!!busy} className="bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="pkg-save-btn">{busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save package</button>
          {cfg.delivery === "build" && (
            <button onClick={build} disabled={!!busy || building} className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="pkg-build-btn">
              {busy === "build" || building ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hammer className="w-4 h-4" />}
              {building ? `Building… ${pkg?.build_progress || pagesCount}/${pkg?.page_count}` : pagesCount >= Number(cfg.page_count) ? "All pages built" : `Build ${Number(cfg.page_count) - pagesCount} pages with AI`}
            </button>
          )}
          {pkg && <button onClick={remove} className="text-sm text-slate-400 hover:text-red-600 px-2 flex items-center gap-1" data-testid="pkg-remove-btn"><Trash2 className="w-4 h-4" />Remove</button>}
          {pkg?.build_error && <p className="w-full text-sm text-red-600" data-testid="pkg-build-error">{pkg.build_error}</p>}
        </div>
      )}
      {pagesCount > 0 && <LandingPagesTable pages={pkg.pages} siteSlug={data.site_slug} audit={audit} ro={ro} onChange={load} />}
    </div>
  );
};
